import React, { useState, useEffect } from 'react';
import { useParentPortal } from '../contexts/ParentPortalContext';
import { useFamilyContext } from '../../../../contexts/FamilyContext';
import { supabase } from '../../../../lib/supabase';
import { Shield, User, Eye, EyeOff, Settings, CheckCircle, XCircle, Users } from 'lucide-react';

const AccessControlSection = () => {
  const { students } = useParentPortal();
  const { currentUser, familyUsers } = useFamilyContext();
  const [accessSettings, setAccessSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  // Load access settings for all students
  useEffect(() => {
    loadAccessSettings();
  }, [students]);

  const loadAccessSettings = async () => {
    if (!students || students.length === 0) return;

    setLoading(true);
    try {
      const studentIds = students.map(s => s.auth_user_id || s.id).filter(Boolean);

      const { data: settings, error } = await supabase
        .from('college_access_settings')
        .select('*')
        .in('child_id', studentIds);

      if (error) throw error;

      // Convert to object indexed by student ID
      const settingsMap = {};
      settings?.forEach(setting => {
        settingsMap[setting.child_id] = setting;
      });

      setAccessSettings(settingsMap);
    } catch (error) {
      console.error('Error loading access settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateAccess = async (studentId, module, hasAccess) => {
    setSaving(prev => ({ ...prev, [`${studentId}-${module}`]: true }));

    try {
      const { error } = await supabase
        .from('college_access_settings')
        .upsert({
          child_id: studentId,
          [`has_${module}_access`]: hasAccess,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'child_id'
        });

      if (error) throw error;

      // Update local state
      setAccessSettings(prev => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          [`has_${module}_access`]: hasAccess
        }
      }));

    } catch (error) {
      console.error('Error updating access:', error);
    } finally {
      setSaving(prev => ({ ...prev, [`${studentId}-${module}`]: false }));
    }
  };

  const modules = [
    {
      id: 'essay',
      name: 'Essay Development',
      description: 'Access to essay writing and refinement tools',
      icon: '📝',
      importance: 'high'
    },
    {
      id: 'college',
      name: 'College Research',
      description: 'College exploration and list building features',
      icon: '🎓',
      importance: 'high'
    },
    {
      id: 'assessment',
      name: 'Strategic Assessment',
      description: 'Self-discovery and strategic positioning tools',
      icon: '🧠',
      importance: 'medium'
    },
    {
      id: 'timeline',
      name: 'Timeline Management',
      description: 'Milestone tracking and deadline management',
      icon: '📅',
      importance: 'medium'
    },
    {
      id: 'sharing',
      name: 'Document Sharing',
      description: 'Ability to share work with parents and counselors',
      icon: '📤',
      importance: 'low'
    }
  ];

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-1/3"></div>
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-slate-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Access Control</h1>
        <p className="text-slate-600 mt-1">
          Manage which college journey features are available to each student
        </p>
      </div>

      {/* Access Philosophy */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-emerald-900 mb-2">
              Graduated Access Philosophy
            </h3>
            <p className="text-emerald-800 mb-3">
              Control access to college journey features based on your student's readiness and your family's approach.
              Start with foundational tools and gradually unlock advanced features as they demonstrate responsibility.
            </p>
            <div className="text-sm text-emerald-700">
              <strong>Recommendation:</strong> Begin with assessment and college research tools,
              then add essay development and timeline management as they progress.
            </div>
          </div>
        </div>
      </div>

      {/* Students Access Grid */}
      {students.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Users className="mx-auto h-12 w-12 text-slate-300 mb-4" />
          <p className="text-slate-600">No students found.</p>
          <p className="text-sm text-slate-500 mt-2">
            Student access controls will appear here when family members are added.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {students.map((student) => {
            const studentId = student.auth_user_id || student.id;
            const settings = accessSettings[studentId] || {};

            return (
              <div key={student.id} className="bg-white rounded-xl border border-slate-200 p-6">
                {/* Student Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-azure-100 to-azure-200 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-azure-700" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {student.name || student.displayName || 'Student'}
                    </h3>
                    <p className="text-slate-600">College Journey Access Controls</p>
                  </div>
                </div>

                {/* Module Access Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {modules.map((module) => {
                    const hasAccess = settings[`has_${module.id}_access`] !== false; // Default to true
                    const isSaving = saving[`${studentId}-${module.id}`];

                    return (
                      <div
                        key={module.id}
                        className={`border-2 rounded-lg p-4 transition-all ${
                          hasAccess
                            ? 'border-emerald-200 bg-emerald-50'
                            : 'border-slate-200 bg-slate-50'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{module.icon}</span>
                            <div>
                              <h4 className="font-medium text-slate-900">{module.name}</h4>
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                module.importance === 'high' ? 'bg-coral-100 text-coral-700' :
                                module.importance === 'medium' ? 'bg-gold-100 text-gold-700' :
                                'bg-azure-100 text-azure-700'
                              }`}>
                                {module.importance} priority
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => updateAccess(studentId, module.id, !hasAccess)}
                            disabled={isSaving}
                            className={`p-2 rounded-lg transition-all ${
                              hasAccess
                                ? 'text-emerald-700 hover:bg-emerald-200'
                                : 'text-slate-400 hover:bg-slate-200'
                            } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {isSaving ? (
                              <div className="w-5 h-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : hasAccess ? (
                              <CheckCircle size={20} />
                            ) : (
                              <XCircle size={20} />
                            )}
                          </button>
                        </div>
                        <p className="text-sm text-slate-600 mb-3">
                          {module.description}
                        </p>
                        <div className={`text-xs font-medium ${
                          hasAccess ? 'text-emerald-700' : 'text-slate-500'
                        }`}>
                          {hasAccess ? 'Access Granted' : 'Access Restricted'}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Quick Actions */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-slate-600">
                      Quick actions for {student.name || student.displayName}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          modules.forEach(module => {
                            updateAccess(studentId, module.id, true);
                          });
                        }}
                        className="px-3 py-1.5 text-sm bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors"
                      >
                        Enable All
                      </button>
                      <button
                        onClick={() => {
                          modules.forEach(module => {
                            updateAccess(studentId, module.id, false);
                          });
                        }}
                        className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                      >
                        Restrict All
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Guidelines */}
      <div className="bg-azure-50 border border-azure-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-azure-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <Settings className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-azure-900 mb-2">
              Access Control Guidelines
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-medium text-azure-900 mb-2">Recommended Starting Point</h4>
                <ul className="text-azure-700 space-y-1">
                  <li>• Strategic Assessment (foundation building)</li>
                  <li>• College Research (exploration phase)</li>
                  <li>• Document Sharing (parent collaboration)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-azure-900 mb-2">Advanced Features</h4>
                <ul className="text-azure-700 space-y-1">
                  <li>• Essay Development (when ready to write)</li>
                  <li>• Timeline Management (application season)</li>
                  <li>• Full feature access (demonstrated responsibility)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessControlSection;